// Context for the tabs component

import { createContext, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { StateTabContext } from './StateTabContext'

export const TabContext = createContext<{
  activeTab: string | null
  setActiveTab: (tab: string | null) => void
  tabs: string[]
  groupName: string
} | null>(null)

export const TabProvider = ({
  children,
  tabs,
  groupName = 'tab',
}: {
  children: React.ReactNode | ((activeTab: string | null) => React.ReactNode)
  tabs: string[]
  groupName?: string
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const tab = searchParams.get(groupName)
    if (tab) {
      setActiveTab(tab)
    } else {
      setActiveTab(tabs[0])
    }
  }, [searchParams])

  const changeTab = (tab: string | null) => {
    // remove all params that do not include "tab" in the name
    const newParams: Record<string, string> = {}
    for (const [key, value] of searchParams.entries()) {
      if (key.includes('tab')) {
        newParams[key] = value
      }
    }

    setSearchParams({ ...newParams, [groupName]: tab }, { replace: true })
    setActiveTab(tab)
  }

  return (
    <TabContext.Provider value={{ activeTab, setActiveTab: changeTab, tabs, groupName }}>
      {typeof children === 'function' ? children(activeTab) : children}
    </TabContext.Provider>
  )
}

export const useTabContext = () => {
  const context = useContext(TabContext)
  const stateContext = useContext(StateTabContext)
  if (!context && !stateContext) {
    throw new Error('useTabContext must be used within a TabProvider')
  }
  return context ?? stateContext
}
